// ── Global State ──

import type { User, GoalWithSubtasks, CalendarEvent, UploadEntry, SubtaskWithResult } from './types';

export const API = window.location.origin;

export let currentUser: User | null = null;
// #3138: counts sign-ins and sign-outs, so a list request started for one
// account is not drawn or cached after another account (or none) took over.
export let sessionGeneration = 0;
function principalKey(u: User | null): string | null {
  return u ? u.id ?? u.email ?? '' : null;
}
export function setCurrentUser(u: User | null) {
  if (principalKey(u) !== principalKey(currentUser)) sessionGeneration += 1;
  currentUser = u;
}

export let currentDeadlineFilter = 'all';
export function setCurrentDeadlineFilter(f: string) { currentDeadlineFilter = f; }

export let currentStatusFilter = 'active';
export function setCurrentStatusFilter(f: string) { currentStatusFilter = f; }

// #2739: the done list reads the most recently completed Goals a page at a
// time; "show more" raises the limit and every reload keeps it.
export const DONE_GOALS_PAGE_SIZE = 50;
export let doneGoalsLimit = DONE_GOALS_PAGE_SIZE;
export function setDoneGoalsLimit(limit: number) { doneGoalsLimit = limit; }
export let doneGoalsHasMore = false;
export function setDoneGoalsHasMore(hasMore: boolean) { doneGoalsHasMore = hasMore; }

export let currentSearchQuery = '';
export function setCurrentSearchQuery(q: string) { currentSearchQuery = q; }

export let currentMainTab = 'today';
export function setCurrentMainTab(tab: string) { currentMainTab = tab; }

export const webExpandedFilterItems = new Set<string>(); // "goalId:d" or "goalId:s" for expanded filter sections

export let cachedAllGoals: GoalWithSubtasks[] = [];
export function setCachedAllGoals(g: GoalWithSubtasks[]) { cachedAllGoals = g; }

export let cachedGoalsJSON: string | null = null;
export function setCachedGoalsJSON(j: string | null) { cachedGoalsJSON = j; }

export const collapsedAiResults = new Set<string>();
export const expandedSubtaskDescriptions = new Set<string>();
export const openRejectFeedbackIds = new Set<string>();

export let uploadFiles: UploadEntry[] = []; // { id, file, dataUrl, status: 'pending'|'analyzing'|'done', summary: null, abortController: null }
export function setUploadFiles(f: UploadEntry[]) { uploadFiles = f; }

export let uploadIdCounter = 0;
export function setUploadIdCounter(c: number) { uploadIdCounter = c; }

export let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // ブラウザ検出、プロフィール保存値で上書き
export function setUserTimezone(tz: string) { userTimezone = tz; }

export const pendingSubtaskGoalIds = new Set<string>(); // goal IDs awaiting Phase 2 subtask generation
export const analyzingUploadIds = new Set<string>(); // upload entry IDs currently in Phase 1 analysis

export let subtaskPollTimer: ReturnType<typeof setInterval> | null = null;
export function setSubtaskPollTimer(t: ReturnType<typeof setInterval> | null) { subtaskPollTimer = t; }

export let loadGoalsErrorCount = 0;
export function setLoadGoalsErrorCount(c: number) { loadGoalsErrorCount = c; }

export let cachedEvents: CalendarEvent[] = [];
export function setCachedEvents(e: CalendarEvent[]) { cachedEvents = e; }

export let isGoalsLoading = false;
export function setIsGoalsLoading(v: boolean) { isGoalsLoading = v; }

// Until the first successful goals load, an empty cachedAllGoals means "not loaded yet", not "zero goals".
export let hasLoadedGoals = false;
export function setHasLoadedGoals(v: boolean) { hasLoadedGoals = v; }

export let isEventsLoading = false;
export function setIsEventsLoading(v: boolean) { isEventsLoading = v; }

export function updateCachedGoal(goalId: string, updater: (goal: GoalWithSubtasks) => GoalWithSubtasks) {
  let didUpdate = false;
  cachedAllGoals = cachedAllGoals.map(goal => {
    if (goal.id !== goalId) return goal;
    didUpdate = true;
    return updater(goal);
  });
  return didUpdate;
}

export function updateCachedSubtask(subtaskId: string, updater: (subtask: SubtaskWithResult) => SubtaskWithResult) {
  let didUpdate = false;
  cachedAllGoals = cachedAllGoals.map(goal => {
    let goalChanged = false;
    const mapItems = (items: SubtaskWithResult[]) => items.map(item => {
      if (item.id !== subtaskId) return item;
      didUpdate = true;
      goalChanged = true;
      return updater(item);
    });
    const decisions = mapItems(goal.decisions || []);
    const subtasks = mapItems(goal.subtasks || []);
    return goalChanged ? { ...goal, decisions, subtasks } : goal;
  });
  return didUpdate;
}

export function removeCachedSubtask(subtaskId: string) {
  let didUpdate = false;
  cachedAllGoals = cachedAllGoals.map(goal => {
    const decisions = (goal.decisions || []).filter(item => item.id !== subtaskId);
    const subtasks = (goal.subtasks || []).filter(item => item.id !== subtaskId);
    if (decisions.length === (goal.decisions || []).length && subtasks.length === (goal.subtasks || []).length) {
      return goal;
    }
    didUpdate = true;
    return { ...goal, decisions, subtasks };
  });
  return didUpdate;
}

export function removeCachedGoal(goalId: string) {
  const nextGoals = cachedAllGoals.filter(goal => goal.id !== goalId);
  const didUpdate = nextGoals.length !== cachedAllGoals.length;
  if (didUpdate) cachedAllGoals = nextGoals;
  return didUpdate;
}
